import { Button } from "@/components/ui/button";
import { isSupportedLocale } from "@/i18n";
import { localizedPath } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { ArrowRight, CalendarRange, Sparkles } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

const CAMPAIGN_YEAR = 2026;
const CAMPAIGN_MONTHS = [9, 10, 11, 12] as const;

type CampaignMonth = (typeof CAMPAIGN_MONTHS)[number];
type CampaignStepState = "complete" | "current" | "upcoming";

type CampaignTimelineProps = {
  placement: "home" | "services";
  /** Kept injectable so the seasonal state can be tested without changing time. */
  now?: Date;
};

function campaignDateInRome(now: Date): { month: number; year: number } {
  const parts = new Intl.DateTimeFormat("en", {
    month: "numeric",
    timeZone: "Europe/Rome",
    year: "numeric",
  }).formatToParts(now);
  return {
    month: Number(parts.find((part) => part.type === "month")?.value),
    year: Number(parts.find((part) => part.type === "year")?.value),
  };
}

function stepState(
  month: CampaignMonth,
  currentMonth: number,
): CampaignStepState {
  if (month < currentMonth) return "complete";
  if (month === currentMonth) return "current";
  return "upcoming";
}

export default function CampaignTimeline({
  placement,
  now = new Date(),
}: CampaignTimelineProps) {
  const { t } = useTranslation("services");
  const { lng } = useParams<{ lng: string }>();
  const locale = isSupportedLocale(lng) ? lng : "it";
  const headingId = useId();
  const { month, year } = campaignDateInRome(now);

  // The campaign removes itself after its published window instead of leaving
  // expired promotional copy on the public site.
  if (
    year !== CAMPAIGN_YEAR ||
    month < CAMPAIGN_MONTHS[0] ||
    month > CAMPAIGN_MONTHS.at(-1)!
  ) {
    return null;
  }

  const discounts = [
    { key: "standard", value: "-40%" },
    { key: "ai", value: "-30%" },
    { key: "pro", value: "-20%" },
  ] as const;

  return (
    <section
      data-enkey-banner={`cyber-days-2026-${placement}`}
      aria-labelledby={headingId}
      className={cn(
        "border-b border-border bg-muted/20",
        placement === "home" && "border-t",
      )}
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] lg:items-end">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-primary">
              <Sparkles aria-hidden="true" className="h-4 w-4" />
              {t("campaign.eyebrow")}
            </span>
            <h2
              id={headingId}
              className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl"
            >
              {t("campaign.title")}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
              {t("campaign.description")}
            </p>
          </div>

          <div
            aria-label={t("campaign.discounts_label")}
            className="grid gap-3 sm:grid-cols-3"
            role="list"
          >
            {discounts.map((discount) => (
              <div
                className="rounded-2xl border border-border bg-background p-5 shadow-sm"
                key={discount.key}
                role="listitem"
              >
                <p className="text-sm font-semibold text-muted-foreground">
                  {t(`campaign.discount.${discount.key}.label`)}
                </p>
                <p className="mt-2 font-display text-4xl font-bold tracking-tight text-primary">
                  {discount.value}
                </p>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  {t(`campaign.discount.${discount.key}.note`)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 rounded-3xl border border-border bg-background p-5 shadow-sm sm:p-7 lg:p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                <CalendarRange aria-hidden="true" className="h-4 w-4" />
                {t("campaign.timeline.eyebrow")}
              </p>
              <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">
                {t("campaign.timeline.title")}
              </h3>
            </div>
            <p className="max-w-xl text-sm leading-6 text-muted-foreground">
              {t("campaign.timeline.description")}
            </p>
          </div>

          <div className="relative mt-8">
            <ol className="grid gap-4 md:grid-cols-4">
              {CAMPAIGN_MONTHS.map((campaignMonth) => {
                const state = stepState(campaignMonth, month);
                const isUpcoming = state === "upcoming";
                return (
                  <li
                    className={cn(
                      "relative rounded-2xl border p-5 transition-colors",
                      state === "current" &&
                        "border-primary bg-primary/5 shadow-lg shadow-primary/10",
                      state === "complete" && "border-border bg-muted/30",
                      isUpcoming && "border-dashed border-border/70 bg-muted/20",
                    )}
                    key={campaignMonth}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "relative z-10 grid h-10 w-10 place-items-center rounded-full border-4 border-background text-xs font-bold",
                        state === "current"
                          ? "bg-primary text-primary-foreground"
                          : state === "complete"
                            ? "bg-foreground text-background"
                            : "bg-muted text-muted-foreground",
                      )}
                    >
                      {String(campaignMonth).padStart(2, "0")}
                    </span>
                    <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      {t(`campaign.timeline.month.${campaignMonth}`)}
                    </p>
                    <span
                      className={cn(
                        "mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                        state === "current"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {t(`campaign.timeline.state.${state}`)}
                    </span>
                    {isUpcoming ? (
                      <div className="mt-4 min-h-14">
                        <p
                          aria-hidden="true"
                          className="select-none font-display text-xl font-bold blur-[5px]"
                        >
                          {t(`campaign.timeline.phase.${campaignMonth}`)}
                        </p>
                        <div
                          aria-hidden="true"
                          className="mt-3 h-2 w-4/5 rounded-full bg-muted-foreground/30 blur-[2px]"
                        />
                        <span className="sr-only">
                          {t("campaign.timeline.hidden")}
                        </span>
                      </div>
                    ) : (
                      <p className="mt-4 font-display text-xl font-bold">
                        {t(`campaign.timeline.phase.${campaignMonth}`)}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="mt-7 flex flex-col gap-5 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-3xl text-xs leading-5 text-muted-foreground">
              {t("campaign.terms")}
            </p>
            <Button asChild className="group shrink-0">
              {placement === "services" ? (
                <a href="#listino">
                  {t("campaign.cta")}
                  <ArrowRight
                    aria-hidden="true"
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
              ) : (
                <Link to={localizedPath("services", locale)}>
                  {t("campaign.cta")}
                  <ArrowRight
                    aria-hidden="true"
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Link>
              )}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
